/**
 * Metadados VISUAIS dos nós do Financeiro nas automações (issue #169).
 *
 * Os campos e rótulos vêm do catálogo do backend (api/automations/financeiro_nodes.py);
 * aqui ficam só o ícone/tom e o resumo de uma linha do card. Separado de
 * nodeMeta.js (que importa este arquivo) para não misturar com os demais módulos.
 */

export const FINANCEIRO_NODE_META = {
  'trigger.finance_entry_created': { icon: 'plus' },
  'trigger.finance_entry_settled': { icon: 'check' },
  'trigger.finance_entry_status_changed': { icon: 'refresh' },
  'trigger.finance_entry_edited': { icon: 'edit' },
  'trigger.finance_entry_deleted': { icon: 'trash' },
  'trigger.finance_receipt_attached': { icon: 'clip' },
  'trigger.payable_overdue': { icon: 'wallet' },
  'trigger.finance_entry_due': { icon: 'bell' },
  'trigger.finance_balance_below': { icon: 'bank' },
  'trigger.finance_cash_negative': { icon: 'alert' },
  'action.finance_create_entry': { icon: 'currency', tone: 'green' },
  'action.finance_set_status': { icon: 'status' },
  'action.finance_settle': { icon: 'ticks', tone: 'green' },
  'action.finance_reschedule': { icon: 'calendar' },
  'action.finance_update_field': { icon: 'edit' },
  'action.finance_next_recurrence': { icon: 'refresh' },
  'action.finance_create_account': { icon: 'bank', tone: 'green' },
}

const trunc = (s, n = 40) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s || '')

const TIPO = { pagar: 'a pagar', receber: 'a receber' }
const STATUS = {
  prevista: 'prevista', paga: 'paga', recebida: 'recebida', atrasada: 'atrasada',
  inadimplente: 'inadimplente', cancelada: 'cancelada',
}
const CAMPO = {
  valor: 'valor', vencimento: 'vencimento', descricao: 'descrição', categoria: 'categoria',
  centro_custo: 'centro de custo', fornecedor: 'fornecedor', documento: 'documento',
}
const NOVO_STATUS = { prevista: 'Volta para prevista', inadimplente: 'Marca inadimplente', cancelada: 'Cancela' }

/** Sufixo de filtro comum dos gatilhos de lançamento: " · a pagar · Infra · ≥ R$ 500". */
function filtros(config) {
  const partes = [
    config.tipo ? TIPO[config.tipo] : null,
    config.categoria || null,
    config.valor_min ? `≥ R$ ${config.valor_min}` : null,
  ].filter(Boolean)
  return partes.length ? ` · ${partes.join(' · ')}` : ''
}

/**
 * Resumo de uma linha dos nós do Financeiro. Devolve `undefined` para tipos que
 * não são do Financeiro (quem chama segue para o resumo dos demais nós).
 */
export function summarizeFinanceiro(key, config = {}) {
  switch (key) {
    case 'trigger.finance_entry_created':
      return `Lançamento criado${filtros(config)}`
    case 'trigger.finance_entry_settled':
      return `Conta paga ou recebida${filtros(config)}`
    case 'trigger.finance_entry_status_changed': {
      const de = config.from_status ? `de ${STATUS[config.from_status]} ` : ''
      const para = config.to_status ? `para ${STATUS[config.to_status]}` : ''
      return `Status ${de}${para}`.trim().replace(/^Status$/, 'Qualquer mudança de status') + filtros(config)
    }
    case 'trigger.finance_entry_edited':
      return config.field ? `Mudou ${CAMPO[config.field] || config.field}${filtros(config)}` : `Lançamento editado${filtros(config)}`
    case 'trigger.finance_entry_deleted':
      return `Lançamento excluído${filtros(config)}`
    case 'trigger.finance_receipt_attached':
      return `Comprovante anexado${filtros(config)}`
    case 'trigger.payable_overdue':
      return (config.min_days > 1 ? `Conta a pagar com ${config.min_days}+ dias de atraso` : 'Conta a pagar vencida') + filtros({ ...config, tipo: '' })
    case 'trigger.finance_entry_due': {
      if (config.days_before == null || config.days_before === '') return 'Escolha quantos dias antes'
      const quando = Number(config.days_before) === 0 ? 'Vence hoje' : `Vence em ${config.days_before} dia(s)`
      return `${quando}${filtros(config)}`
    }
    case 'trigger.finance_balance_below':
      return config.limit != null && config.limit !== '' ? `Saldo abaixo de R$ ${config.limit}` : 'Informe o limite'
    case 'trigger.finance_cash_negative':
      return 'Projeção de caixa negativa'
    case 'action.finance_create_entry': {
      if (!config.tipo) return 'Escolha o tipo'
      const parcelas = Number(config.parcelas) > 1 ? ` em ${config.parcelas}x` : ''
      return `Cria conta ${TIPO[config.tipo]}${config.valor ? ` de R$ ${trunc(config.valor, 14)}` : ''}${parcelas}`
    }
    case 'action.finance_set_status':
      return NOVO_STATUS[config.status] || 'Escolha o novo status'
    case 'action.finance_settle':
      return config.update_balance === 'sim' ? 'Dá baixa e atualiza o saldo' : 'Dá baixa no lançamento'
    case 'action.finance_reschedule': {
      if (config.mode === 'fixed') return config.date ? `Vence em ${trunc(config.date, 16)}` : 'Defina a nova data'
      if (config.mode === 'shift' && config.amount) {
        const un = { days: 'dia(s)', weeks: 'semana(s)', months: 'mês(es)' }[config.unit || 'days']
        return `${config.direction === 'earlier' ? 'Adianta' : 'Adia'} ${config.amount} ${un}`
      }
      return 'Escolha como reagendar'
    }
    case 'action.finance_update_field':
      return config.field ? `Altera ${CAMPO[config.field] || config.field}` : 'Escolha o campo'
    case 'action.finance_next_recurrence':
      return 'Cria o lançamento do mês seguinte'
    case 'action.finance_create_account':
      return config.nome ? `Cria conta “${trunc(config.nome, 30)}”` : 'Informe o nome da conta'
    default:
      return undefined
  }
}
